import type { Membership, Permission, Role } from '../types';

// RBAC gating for FE surfaces. Gate on a Permission (SC-2620 convention), never
// on a raw role string, so per-org permission overrides (a future BE feature)
// don't require touching call sites.
//
// The BE is the source of truth: once it surfaces `permissions` on the active
// membership (the SSO-invite BE slice adds it to `MembershipResponse` on `/me`
// + auth responses), `membershipHasPermission` reads that list directly. Until
// then it falls back to deriving the permission from the caller's role on the
// org ladder — so call sites (`membershipHasPermission(membership, 'invite_member')`)
// are already correct and need no change when the field lands; the fallback then
// becomes dead code we can delete.

// Ascending privilege on the ORG axis only. EKOM_ADMIN is deliberately absent —
// it's the platform axis and does NOT satisfy an org-role minimum (staff act via
// the admin console, not tenant surfaces), mirroring the BE `_ORG_LADDER`.
const ORG_RANK: Partial<Record<Role, number>> = {
  org_user: 0,
  org_admin: 1,
  org_owner: 2,
};

// Minimum org role that confers each permission when the BE hasn't sent an
// explicit permission list yet. Mirrors the BE `ROLE_PERMISSIONS` floors
// (SC-2620) — INVITE_MEMBER is ORG_OWNER-gated (SC-2619 Fork A).
//
// `null` = NO role fallback: the permission is honored only from the BE list.
// For a permission whose floor is a per-org policy, any role mapping here is a
// guess that could show a control the org has switched off.
const PERMISSION_ROLE_FLOOR: Record<Permission, Role | null> = {
  invite_member: 'org_owner',
  // Mirrors the BE `MANAGE_DESTINATION_MEMBERSHIP: Role.ORG_ADMIN` floor. The
  // *read* side (`GET /destinations`) is ungated on purpose — any active member
  // who can see a row must be able to name the channel it belongs to — so only
  // the write is gated here.
  manage_destination_membership: 'org_admin',
  // BE `MANAGE_DESTINATIONS: Role.ORG_ADMIN` (SC-3121) — the destination's own
  // config, incl. the kill switch. Deliberately separate from membership above.
  manage_destinations: 'org_admin',
  // BE `VIEW_SOURCES: Role.ORG_ADMIN` — the whole sources read API sits behind it.
  view_sources: 'org_admin',
  // BE `EDIT_ENTITY_VALUE` (SC-3106) — one attributed, reversible cell edit. The
  // BE floor is ORG_USER today, but it is a per-org policy orgs will be able to
  // tighten, so the SC-3106 contract gates the control on the `/me` permission
  // list ONLY, never the role (Erin, 2026-10-07).
  edit_entity_value: null,
  // BE `EDIT_MEMBER_TITLE: Role.ORG_ADMIN` (SC-2882) — the roster title write.
  // Reading the roster itself is ungated (any active member, SC-3227).
  edit_member_title: 'org_admin',
  // BE `APPLY_FINDING` — applying a finding at any scope. ORG_USER by default
  // (SC-3422, Erin 2026-10-12) but a per-org policy, so, like `edit_entity_value`,
  // the Apply controls read the `/me` permission list ONLY, never the role.
  apply_finding: null,
};

/**
 * Does this membership confer `permission`? Prefers the BE-supplied permission
 * list; falls back to a role-ladder derivation until the BE ships it.
 */
export function membershipHasPermission(
  membership: Membership | null | undefined,
  permission: Permission,
): boolean {
  // A pending or inactive membership confers nothing — regardless of role or a
  // stale permission list, only an ACTIVE membership can act on the org.
  if (!membership || membership.status !== 'active') return false;

  // Source of truth once present.
  if (membership.permissions) {
    return membership.permissions.includes(permission);
  }

  // Fallback: role at or above the permission's floor on the org ladder.
  const floor = PERMISSION_ROLE_FLOOR[permission];
  if (floor === null) return false;
  const floorRank = ORG_RANK[floor];
  const roleRank = ORG_RANK[membership.role];
  if (floorRank === undefined || roleRank === undefined) return false;
  return roleRank >= floorRank;
}
